import { ref } from "vue"
import {
  getActiveThemeAdvancedControls,
  getActiveThemeBrightness,
  getActiveThemeBrightnessEnabled,
  getActiveThemeColor,
  getActiveThemeDensity,
  getActiveThemeDensityScale,
  getActiveThemeFont,
  getActiveThemeFontSize,
  getActiveThemeFontWeight,
  getActiveThemeName,
  getActiveThemePerformanceMode,
  getActiveThemeProfile,
  getActiveThemeSurfaceEffect,
  getBusinessAllCategoryFlatMode,
  getBusinessProductCardDensityLinked,
  getBusinessProductCardViewMode,
  getBusinessTableCardDensity,
  getBusinessTableCardDensityLinked,
  setActiveThemeAdvancedControls,
  setActiveThemeBrightness,
  setActiveThemeBrightnessEnabled,
  setActiveThemeColor,
  setActiveThemeDensity,
  setActiveThemeDensityScale,
  setActiveThemeFont,
  setActiveThemeFontSize,
  setActiveThemeFontWeight,
  setActiveThemeName,
  setActiveThemePerformanceMode,
  setActiveThemeProfile,
  setActiveThemeSurfaceEffect,
  setBusinessAllCategoryFlatMode as setStoredBusinessAllCategoryFlatMode,
  setBusinessProductCardDensityLinked as setStoredBusinessProductCardDensityLinked,
  setBusinessProductCardViewMode as setStoredBusinessProductCardViewMode,
  setBusinessTableCardDensity as setStoredBusinessTableCardDensity,
  setBusinessTableCardDensityLinked as setStoredBusinessTableCardDensityLinked,
} from "@/utils/cache/localStorage"
import {
  applyThemeColorScheme,
  DEEP_BLUE_THEME_COLOR,
  DEFAULT_THEME_COLOR,
  normalizeHexColor,
  type ThemeColorMode,
} from "@/utils/theme/colorScheme"

const DEFAULT_THEME_NAME = "normal"
const SYSTEM_THEME_NAME = "system"
const LEGACY_DARK_BLUE_THEME_NAME = "dark-blue"
const LEGACY_VITALITY_RED_THEME_COLOR = "#C9584A"
const VITALITY_RED_THEME_COLOR = "#E5483D"
const LEGACY_YAO_GOLD_THEME_COLOR = "#B68A1F"
const BLACK_GOLD_THEME_COLOR = "#F4D8A7"
const LEGACY_OBSIDIAN_THEME_COLOR = "#2B3137"
const REMOVED_TEA_BROWN_THEME_COLOR = "#8A6A46"
const REMOVED_ELEGANT_THEME_COLOR = "#76716A"
const THEME_CLASS_NAMES = ["normal", "dark"] as const
const THEME_PROFILE_NAMES = ["default", "elegant", "eye-care"] as const
const REMOVABLE_THEME_CLASS_NAMES = [...THEME_CLASS_NAMES, LEGACY_DARK_BLUE_THEME_NAME] as const
const THEME_FONT_NAMES = ["standard", "large", "strong"] as const
const THEME_DENSITY_NAMES = ["compact", "standard", "comfortable"] as const
const THEME_SURFACE_EFFECT_NAMES = ["standard", "soft", "glass"] as const
const BUSINESS_TABLE_CARD_DENSITY_NAMES = ["compact", "standard", "comfortable"] as const
const BUSINESS_PRODUCT_CARD_VIEW_MODE_NAMES = ["compact", "standard", "comfortable"] as const
const THEME_FONT_SIZE_VALUES = [13, 14, 15, 16] as const
const THEME_FONT_WEIGHT_VALUES = [400, 500, 600, 700] as const
const THEME_DENSITY_SCALE_VALUES = [0, 1, 2] as const
const THEME_BRIGHTNESS_MIN = 70
const THEME_BRIGHTNESS_MAX = 130
const DEFAULT_THEME_BRIGHTNESS = 100
const DEFAULT_THEME_BRIGHTNESS_ENABLED = false
const DEFAULT_THEME_PERFORMANCE_MODE = true
const THEME_FONT_CLASS_NAMES = THEME_FONT_NAMES.map(name => `theme-font-${name}`)
const THEME_DENSITY_CLASS_NAMES = THEME_DENSITY_NAMES.map(name => `theme-density-${name}`)
const THEME_SURFACE_EFFECT_CLASS_NAMES = THEME_SURFACE_EFFECT_NAMES.map(name => `theme-surface-${name}`)

/** 注册的主题名称, 其中 DefaultThemeNameType 是必填的 */
export type ThemeName = typeof THEME_CLASS_NAMES[number]
export type ThemePreference = ThemeName | typeof SYSTEM_THEME_NAME
export type ThemeProfileName = typeof THEME_PROFILE_NAMES[number]
export type ThemeFontName = typeof THEME_FONT_NAMES[number]
export type ThemeDensityName = typeof THEME_DENSITY_NAMES[number]
export type ThemeSurfaceEffectName = typeof THEME_SURFACE_EFFECT_NAMES[number]
export type ThemeFontSize = typeof THEME_FONT_SIZE_VALUES[number]
export type ThemeFontWeight = typeof THEME_FONT_WEIGHT_VALUES[number]
export type ThemeDensityScale = typeof THEME_DENSITY_SCALE_VALUES[number]
export type BusinessTableCardDensity = typeof BUSINESS_TABLE_CARD_DENSITY_NAMES[number]
export type BusinessProductCardViewMode = typeof BUSINESS_PRODUCT_CARD_VIEW_MODE_NAMES[number]

interface IThemeList {
  title: string
  name: ThemePreference
  description: string
}

/** 主题列表 */
const themeList: IThemeList[] = [
  {
    title: "明亮",
    name: DEFAULT_THEME_NAME,
    description: "高密度浅色后台主题",
  },
  {
    title: "暗黑",
    name: "dark",
    description: "低亮度深色主题",
  },
  {
    title: "跟随系统",
    name: SYSTEM_THEME_NAME,
    description: "按系统浅色/深色偏好切换",
  },
]

interface ThemeColorPreset {
  title: string
  value: string
}

interface ThemeProfileOption {
  title: string
  name: ThemeProfileName
  description: string
  themeName: ThemePreference
  color: string
  surfaceEffect: ThemeSurfaceEffectName
  brightness: number
}

interface ThemeFontOption {
  title: string
  name: ThemeFontName
  description: string
}

interface ThemeDensityOption {
  title: string
  name: ThemeDensityName
  description: string
}

interface ThemeSurfaceEffectOption {
  title: string
  name: ThemeSurfaceEffectName
  description: string
}

interface BusinessTableCardDensityOption {
  title: string
  name: BusinessTableCardDensity
  description: string
}

interface BusinessProductCardViewModeOption {
  title: string
  name: BusinessProductCardViewMode
  description: string
}

interface ThemeSliderMark<T extends number = number> {
  title: string
  value: T
  description: string
}

interface ThemeDensityScaleOption extends ThemeSliderMark<ThemeDensityScale> {
  name: ThemeDensityName
  spacing: string
  controlSize: string
}

interface ThemeFontSizeScale {
  caption: string
  bodySm: string
  bodyMd: string
  bodyLg: string
  titleLg: string
  headlineMd: string
  stat: string
  metric: string
}

const themeColorPresets: ThemeColorPreset[] = [
  { title: "品牌蓝", value: DEFAULT_THEME_COLOR },
  { title: "翠绿", value: "#00A870" },
  { title: "黑金", value: BLACK_GOLD_THEME_COLOR },
]
const themeColorPresetValues = themeColorPresets.map(preset => preset.value)

const themeProfileList: ThemeProfileOption[] = [
  {
    title: "默认",
    name: "default",
    description: "明亮 + 品牌蓝 + 净面",
    themeName: DEFAULT_THEME_NAME,
    color: DEFAULT_THEME_COLOR,
    surfaceEffect: "standard",
    brightness: DEFAULT_THEME_BRIGHTNESS,
  },
  {
    title: "黑金",
    name: "elegant",
    description: "暗黑 + 黑金 + 净面",
    themeName: "dark",
    color: BLACK_GOLD_THEME_COLOR,
    surfaceEffect: "standard",
    brightness: DEFAULT_THEME_BRIGHTNESS,
  },
  {
    title: "护眼",
    name: "eye-care",
    description: "明亮 + 翠绿 + 纸纹",
    themeName: DEFAULT_THEME_NAME,
    color: "#00A870",
    surfaceEffect: "soft",
    brightness: DEFAULT_THEME_BRIGHTNESS,
  },
]

const themeFontList: ThemeFontOption[] = [
  {
    title: "标准",
    name: "standard",
    description: "默认字号和字重",
  },
  {
    title: "大字",
    name: "large",
    description: "整体字号上调",
  },
  {
    title: "醒目",
    name: "strong",
    description: "字号略大且字重更强",
  },
]

const themeDensityList: ThemeDensityOption[] = [
  {
    title: "紧凑",
    name: "compact",
    description: "减少留白，提高信息密度",
  },
  {
    title: "标准",
    name: "standard",
    description: "默认后台密度",
  },
  {
    title: "宽松",
    name: "comfortable",
    description: "增加留白和控件高度",
  },
]

const themeSurfaceEffectList: ThemeSurfaceEffectOption[] = [
  {
    title: "净面",
    name: "standard",
    description: "低眩光基底",
  },
  {
    title: "纸纹",
    name: "soft",
    description: "细纹理柔和铺底",
  },
  {
    title: "磨砂",
    name: "glass",
    description: "透光纹理层次",
  },
]

const businessTableCardDensityList: BusinessTableCardDensityOption[] = [
  {
    title: "紧凑",
    name: "compact",
    description: "短卡片高密度浏览",
  },
  {
    title: "标准",
    name: "standard",
    description: "完整桌台信息",
  },
  {
    title: "宽松",
    name: "comfortable",
    description: "更高卡片留白",
  },
]

const businessProductCardViewModeList: BusinessProductCardViewModeOption[] = [
  {
    title: "紧凑",
    name: "compact",
    description: "小卡片双行名称",
  },
  {
    title: "标准",
    name: "standard",
    description: "默认商品卡片",
  },
  {
    title: "宽松",
    name: "comfortable",
    description: "大卡片易点选",
  },
]

const themeFontSizeList: ThemeSliderMark<ThemeFontSize>[] = [
  { title: "小", value: 13, description: "13px" },
  { title: "标准", value: 14, description: "14px" },
  { title: "大", value: 15, description: "15px" },
  { title: "超大", value: 16, description: "16px" },
]

const themeFontWeightList: ThemeSliderMark<ThemeFontWeight>[] = [
  { title: "常规", value: 400, description: "400" },
  { title: "中等", value: 500, description: "500" },
  { title: "半粗", value: 600, description: "600" },
  { title: "粗体", value: 700, description: "700" },
]

const themeDensityScaleList: ThemeDensityScaleOption[] = [
  {
    title: "紧凑",
    name: "compact",
    value: 0,
    description: "页面 12px / 控件 36px",
    spacing: "12px",
    controlSize: "36px",
  },
  {
    title: "标准",
    name: "standard",
    value: 1,
    description: "页面 20px / 控件 40px",
    spacing: "20px",
    controlSize: "40px",
  },
  {
    title: "宽松",
    name: "comfortable",
    value: 2,
    description: "页面 24px / 控件 44px",
    spacing: "24px",
    controlSize: "44px",
  },
]

const themeFontSizeScaleMap: Record<ThemeFontSize, ThemeFontSizeScale> = {
  13: {
    caption: "12px",
    bodySm: "12px",
    bodyMd: "13px",
    bodyLg: "15px",
    titleLg: "19px",
    headlineMd: "23px",
    stat: "27px",
    metric: "31px",
  },
  14: {
    caption: "12px",
    bodySm: "13px",
    bodyMd: "14px",
    bodyLg: "16px",
    titleLg: "20px",
    headlineMd: "24px",
    stat: "28px",
    metric: "32px",
  },
  15: {
    caption: "13px",
    bodySm: "14px",
    bodyMd: "15px",
    bodyLg: "17px",
    titleLg: "21px",
    headlineMd: "25px",
    stat: "30px",
    metric: "34px",
  },
  16: {
    caption: "14px",
    bodySm: "15px",
    bodyMd: "16px",
    bodyLg: "18px",
    titleLg: "22px",
    headlineMd: "26px",
    stat: "32px",
    metric: "36px",
  },
}

const isThemeName = (value: string | null | undefined): value is ThemeName => {
  return THEME_CLASS_NAMES.includes(value as ThemeName)
}

const isThemePreference = (value: string | null | undefined): value is ThemePreference => {
  return isThemeName(value) || value === SYSTEM_THEME_NAME
}

const isThemeProfileName = (value: string | null | undefined): value is ThemeProfileName => {
  return THEME_PROFILE_NAMES.includes(value as ThemeProfileName)
}

const isThemeFontName = (value: string | null | undefined): value is ThemeFontName => {
  return THEME_FONT_NAMES.includes(value as ThemeFontName)
}

const isThemeDensityName = (value: string | null | undefined): value is ThemeDensityName => {
  return THEME_DENSITY_NAMES.includes(value as ThemeDensityName)
}

const isThemeSurfaceEffectName = (value: string | null | undefined): value is ThemeSurfaceEffectName => {
  return THEME_SURFACE_EFFECT_NAMES.includes(value as ThemeSurfaceEffectName)
}

const isBusinessTableCardDensity = (value: string | null | undefined): value is BusinessTableCardDensity => {
  return BUSINESS_TABLE_CARD_DENSITY_NAMES.includes(value as BusinessTableCardDensity)
}

const isBusinessProductCardViewMode = (value: string | null | undefined): value is BusinessProductCardViewMode => {
  return BUSINESS_PRODUCT_CARD_VIEW_MODE_NAMES.includes(value as BusinessProductCardViewMode)
}

const isThemeFontSize = (value: number): value is ThemeFontSize => {
  return THEME_FONT_SIZE_VALUES.includes(value as ThemeFontSize)
}

const isThemeFontWeight = (value: number): value is ThemeFontWeight => {
  return THEME_FONT_WEIGHT_VALUES.includes(value as ThemeFontWeight)
}

const isThemeDensityScale = (value: number): value is ThemeDensityScale => {
  return THEME_DENSITY_SCALE_VALUES.includes(value as ThemeDensityScale)
}

const normalizeNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return null
  }
  const normalized = Number(value)
  return Number.isFinite(normalized) ? normalized : null
}

const normalizeThemeFontSize = (value: string | number | null | undefined): ThemeFontSize | null => {
  const normalized = normalizeNumber(value)
  return normalized !== null && isThemeFontSize(normalized) ? normalized : null
}

const normalizeThemeFontWeight = (value: string | number | null | undefined): ThemeFontWeight | null => {
  const normalized = normalizeNumber(value)
  return normalized !== null && isThemeFontWeight(normalized) ? normalized : null
}

const normalizeThemeDensityScale = (value: string | number | null | undefined): ThemeDensityScale | null => {
  const normalized = normalizeNumber(value)
  return normalized !== null && isThemeDensityScale(normalized) ? normalized : null
}

const normalizeThemeBrightness = (value: string | number | null | undefined) => {
  const normalized = normalizeNumber(value)
  if (normalized === null) {
    return DEFAULT_THEME_BRIGHTNESS
  }
  return Math.min(THEME_BRIGHTNESS_MAX, Math.max(THEME_BRIGHTNESS_MIN, Math.round(normalized)))
}

const getThemeFontFromControls = (fontSize: ThemeFontSize, fontWeight: ThemeFontWeight): ThemeFontName => {
  if (fontWeight >= 500) {
    return "strong"
  }
  return fontSize >= 15 ? "large" : "standard"
}

const getThemeFontControlsFromName = (value: ThemeFontName): { fontSize: ThemeFontSize, fontWeight: ThemeFontWeight } => {
  if (value === "strong") {
    return { fontSize: 15, fontWeight: 500 }
  }
  if (value === "large") {
    return { fontSize: 15, fontWeight: 400 }
  }
  return { fontSize: 14, fontWeight: 400 }
}

const getThemeDensityScaleFromName = (value: ThemeDensityName): ThemeDensityScale => {
  return themeDensityScaleList.find(item => item.name === value)?.value ?? 1
}

const getThemeDensityNameFromScale = (value: ThemeDensityScale): ThemeDensityName => {
  return themeDensityScaleList.find(item => item.value === value)?.name ?? "standard"
}

const getBusinessDensityFromThemeDensityScale = (value: ThemeDensityScale): BusinessTableCardDensity => {
  if (value === 0) {
    return "compact"
  }
  if (value === 2) {
    return "comfortable"
  }
  return "standard"
}

const getBusinessProductCardViewModeFromThemeDensityScale = (value: ThemeDensityScale): BusinessProductCardViewMode => {
  if (value === 0) {
    return "compact"
  }
  if (value === 2) {
    return "comfortable"
  }
  return "standard"
}

const getSystemThemeName = (): ThemeName => {
  if (typeof window === "undefined" || !window.matchMedia) {
    return DEFAULT_THEME_NAME
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : DEFAULT_THEME_NAME
}

const resolveThemeName = (preference: ThemePreference): ThemeName => {
  return preference === SYSTEM_THEME_NAME ? getSystemThemeName() : preference
}

const resolveThemeColorMode = (themeName: ThemeName): ThemeColorMode => {
  return themeName === DEFAULT_THEME_NAME ? "light" : "dark"
}

const isDarkThemeName = (themeName: ThemeName) => {
  return resolveThemeColorMode(themeName) === "dark"
}

const getStoredThemePreference = (): ThemePreference => {
  const storedTheme = getActiveThemeName()
  if (storedTheme === LEGACY_DARK_BLUE_THEME_NAME) {
    return "dark"
  }
  return isThemePreference(storedTheme) ? storedTheme : DEFAULT_THEME_NAME
}

const getStoredThemeColor = () => {
  const storedTheme = getActiveThemeName()
  const storedColor = normalizeHexColor(getActiveThemeColor())
  if (storedColor === LEGACY_VITALITY_RED_THEME_COLOR) {
    return VITALITY_RED_THEME_COLOR
  }
  if (storedColor === LEGACY_YAO_GOLD_THEME_COLOR) {
    return BLACK_GOLD_THEME_COLOR
  }
  if (storedColor === LEGACY_OBSIDIAN_THEME_COLOR) {
    return BLACK_GOLD_THEME_COLOR
  }
  if (storedColor === REMOVED_TEA_BROWN_THEME_COLOR || storedColor === REMOVED_ELEGANT_THEME_COLOR) {
    return DEFAULT_THEME_COLOR
  }
  return storedColor || (storedTheme === LEGACY_DARK_BLUE_THEME_NAME ? DEEP_BLUE_THEME_COLOR : DEFAULT_THEME_COLOR)
}

const getStoredThemeProfile = (): ThemeProfileName => {
  const storedThemeProfile = getActiveThemeProfile()
  return isThemeProfileName(storedThemeProfile) ? storedThemeProfile : "default"
}

const getStoredThemeFont = (): ThemeFontName => {
  const storedThemeFont = getActiveThemeFont()
  return isThemeFontName(storedThemeFont) ? storedThemeFont : "standard"
}

const getStoredThemeFontSize = (): ThemeFontSize => {
  return normalizeThemeFontSize(getActiveThemeFontSize())
    || getThemeFontControlsFromName(getStoredThemeFont()).fontSize
}

const getStoredThemeFontWeight = (): ThemeFontWeight => {
  return normalizeThemeFontWeight(getActiveThemeFontWeight())
    || getThemeFontControlsFromName(getStoredThemeFont()).fontWeight
}

const getStoredThemeDensity = (): ThemeDensityName => {
  const storedThemeDensity = getActiveThemeDensity()
  return isThemeDensityName(storedThemeDensity) ? storedThemeDensity : "standard"
}

const getStoredThemeDensityScale = (): ThemeDensityScale => {
  return normalizeThemeDensityScale(getActiveThemeDensityScale())
    ?? getThemeDensityScaleFromName(getStoredThemeDensity())
}

const getStoredThemeSurfaceEffect = (): ThemeSurfaceEffectName => {
  const storedThemeSurfaceEffect = getActiveThemeSurfaceEffect()
  return isThemeSurfaceEffectName(storedThemeSurfaceEffect) ? storedThemeSurfaceEffect : "standard"
}

const getThemeProfileDefaultBrightness = (profileName: ThemeProfileName) => {
  return themeProfileList.find(profile => profile.name === profileName)?.brightness ?? DEFAULT_THEME_BRIGHTNESS
}

const getStoredThemeBrightness = () => {
  const storedThemeBrightness = getActiveThemeBrightness()
  if (storedThemeBrightness === null) {
    return getThemeProfileDefaultBrightness(getStoredThemeProfile())
  }
  return normalizeThemeBrightness(storedThemeBrightness)
}

const getStoredBusinessTableCardDensity = (): BusinessTableCardDensity => {
  const storedDensity = getBusinessTableCardDensity()
  return isBusinessTableCardDensity(storedDensity) ? storedDensity : "standard"
}

const getStoredBusinessProductCardViewMode = (): BusinessProductCardViewMode => {
  const storedViewMode = getBusinessProductCardViewMode()
  if (storedViewMode === "normal") {
    return "standard"
  }
  return isBusinessProductCardViewMode(storedViewMode) ? storedViewMode : "standard"
}

const getStoredBooleanPreference = (value: string | null, fallback: boolean) => {
  if (value === null) {
    return fallback
  }
  return value === "true"
}

/** 正在应用的主题名称 */
const activeThemeName = ref<ThemePreference>(getStoredThemePreference())
const resolvedThemeName = ref<ThemeName>(resolveThemeName(activeThemeName.value))
const activeThemeProfile = ref<ThemeProfileName>(getStoredThemeProfile())
const activeThemeColor = ref(getStoredThemeColor())
const activeThemeFont = ref<ThemeFontName>(getStoredThemeFont())
const activeThemeFontSize = ref<ThemeFontSize>(getStoredThemeFontSize())
const activeThemeFontWeight = ref<ThemeFontWeight>(getStoredThemeFontWeight())
const activeThemeDensity = ref<ThemeDensityName>(getStoredThemeDensity())
const activeThemeDensityScale = ref<ThemeDensityScale>(getStoredThemeDensityScale())
const activeThemeSurfaceEffect = ref<ThemeSurfaceEffectName>(getStoredThemeSurfaceEffect())
const activeThemeBrightness = ref(getStoredThemeBrightness())
const activeThemeBrightnessEnabled = ref(getStoredBooleanPreference(getActiveThemeBrightnessEnabled(), DEFAULT_THEME_BRIGHTNESS_ENABLED))
const activeThemePerformanceMode = ref(getStoredBooleanPreference(getActiveThemePerformanceMode(), DEFAULT_THEME_PERFORMANCE_MODE))
const activeThemeAdvancedControls = ref(getStoredBooleanPreference(getActiveThemeAdvancedControls(), false))
const activeBusinessAllCategoryFlatMode = ref(getBusinessAllCategoryFlatMode())
const activeBusinessTableCardDensity = ref<BusinessTableCardDensity>(getStoredBusinessTableCardDensity())
const activeBusinessProductCardViewMode = ref<BusinessProductCardViewMode>(getStoredBusinessProductCardViewMode())
const activeBusinessTableCardDensityLinked = ref(getStoredBooleanPreference(getBusinessTableCardDensityLinked(), true))
const activeBusinessProductCardDensityLinked = ref(getStoredBooleanPreference(getBusinessProductCardDensityLinked(), true))
let systemThemeListenerRegistered = false

const initTheme = () => {
  ensureSystemThemeListener()
  applyTheme(activeThemeName.value)
  applyThemeTypography(activeThemeFontSize.value, activeThemeFontWeight.value)
  applyThemeDensityScale(activeThemeDensityScale.value)
  applyThemeSurfaceEffect(activeThemeSurfaceEffect.value)
  applyThemePerformanceMode(activeThemePerformanceMode.value)
  applyThemeBrightness(activeThemeBrightness.value)
  syncLinkedBusinessDensityPreferences()
  const storedThemeColor = normalizeHexColor(getActiveThemeColor())
  const shouldPersistMigratedColor = storedThemeColor === LEGACY_VITALITY_RED_THEME_COLOR
    || storedThemeColor === LEGACY_YAO_GOLD_THEME_COLOR
    || storedThemeColor === LEGACY_OBSIDIAN_THEME_COLOR
    || storedThemeColor === REMOVED_TEA_BROWN_THEME_COLOR
    || storedThemeColor === REMOVED_ELEGANT_THEME_COLOR
  if (getActiveThemeName() === LEGACY_DARK_BLUE_THEME_NAME || shouldPersistMigratedColor) {
    setActiveThemeName(activeThemeName.value)
    setActiveThemeColor(activeThemeColor.value)
  }
  if (!getActiveThemeProfile()) {
    setActiveThemeProfile(activeThemeProfile.value)
  }
}

const setTheme = (value: ThemePreference) => {
  const nextTheme = isThemePreference(value) ? value : DEFAULT_THEME_NAME
  activeThemeName.value = nextTheme
  applyTheme(nextTheme)
  setActiveThemeName(nextTheme)
}

const applyTheme = (value: ThemePreference) => {
  activeThemeName.value = value
  resolvedThemeName.value = resolveThemeName(value)
  setHtmlClassName(resolvedThemeName.value, value)
  applyThemeColorScheme(activeThemeColor.value, resolveThemeColorMode(resolvedThemeName.value))
}

const setThemeColor = (value: string | null | undefined) => {
  const nextColor = normalizeHexColor(value) || DEFAULT_THEME_COLOR
  activeThemeColor.value = nextColor
  applyThemeColorScheme(nextColor, resolveThemeColorMode(resolvedThemeName.value))
  setActiveThemeColor(nextColor)
}

const resetThemeColor = () => {
  setThemeColor(DEFAULT_THEME_COLOR)
}

const setThemeFont = (value: ThemeFontName) => {
  const nextFont = isThemeFontName(value) ? value : "standard"
  const nextControls = getThemeFontControlsFromName(nextFont)
  activeThemeFont.value = nextFont
  activeThemeFontSize.value = nextControls.fontSize
  activeThemeFontWeight.value = nextControls.fontWeight
  applyThemeTypography(nextControls.fontSize, nextControls.fontWeight)
  setActiveThemeFont(nextFont)
  setActiveThemeFontSize(nextControls.fontSize)
  setActiveThemeFontWeight(nextControls.fontWeight)
}

const setThemeFontSize = (value: number) => {
  const nextFontSize = normalizeThemeFontSize(value) || 14
  activeThemeFontSize.value = nextFontSize
  activeThemeFont.value = getThemeFontFromControls(nextFontSize, activeThemeFontWeight.value)
  applyThemeTypography(nextFontSize, activeThemeFontWeight.value)
  setActiveThemeFont(activeThemeFont.value)
  setActiveThemeFontSize(nextFontSize)
}

const setThemeFontWeight = (value: number) => {
  const nextFontWeight = normalizeThemeFontWeight(value) || 400
  activeThemeFontWeight.value = nextFontWeight
  activeThemeFont.value = getThemeFontFromControls(activeThemeFontSize.value, nextFontWeight)
  applyThemeTypography(activeThemeFontSize.value, nextFontWeight)
  setActiveThemeFont(activeThemeFont.value)
  setActiveThemeFontWeight(nextFontWeight)
}

const setThemeDensity = (value: ThemeDensityName) => {
  const nextDensity = isThemeDensityName(value) ? value : "standard"
  const nextDensityScale = getThemeDensityScaleFromName(nextDensity)
  activeThemeDensity.value = nextDensity
  activeThemeDensityScale.value = nextDensityScale
  applyThemeDensity(nextDensity)
  setActiveThemeDensity(nextDensity)
  setActiveThemeDensityScale(nextDensityScale)
  syncLinkedBusinessDensityPreferences()
}

const setThemeDensityScale = (value: number) => {
  const nextDensityScale = normalizeThemeDensityScale(value) ?? 1
  const nextDensity = getThemeDensityNameFromScale(nextDensityScale)
  activeThemeDensityScale.value = nextDensityScale
  activeThemeDensity.value = nextDensity
  applyThemeDensity(nextDensity)
  setActiveThemeDensity(nextDensity)
  setActiveThemeDensityScale(nextDensityScale)
  syncLinkedBusinessDensityPreferences()
}

const setThemeSurfaceEffect = (value: ThemeSurfaceEffectName) => {
  const nextSurfaceEffect = isThemeSurfaceEffectName(value) ? value : "standard"
  activeThemeSurfaceEffect.value = nextSurfaceEffect
  applyThemeSurfaceEffect(nextSurfaceEffect)
  setActiveThemeSurfaceEffect(nextSurfaceEffect)
}

const setThemeProfile = (value: ThemeProfileName) => {
  const nextProfile = themeProfileList.find(profile => profile.name === value) || themeProfileList[0]
  activeThemeProfile.value = nextProfile.name
  setTheme(nextProfile.themeName)
  setThemeColor(nextProfile.color)
  setThemeSurfaceEffect(nextProfile.surfaceEffect)
  setThemeBrightnessEnabled(DEFAULT_THEME_BRIGHTNESS_ENABLED)
  setThemeBrightness(nextProfile.brightness)
  setActiveThemeProfile(nextProfile.name)
}

const setThemeBrightness = (value: string | number | null | undefined) => {
  const nextBrightness = normalizeThemeBrightness(value)
  activeThemeBrightness.value = nextBrightness
  applyThemeBrightness(nextBrightness)
  setActiveThemeBrightness(nextBrightness)
}

const setThemeBrightnessEnabled = (value: boolean) => {
  activeThemeBrightnessEnabled.value = value
  applyThemeBrightness(activeThemeBrightness.value)
  setActiveThemeBrightnessEnabled(value)
}

const setThemePerformanceMode = (value: boolean) => {
  activeThemePerformanceMode.value = value
  applyThemePerformanceMode(value)
  applyThemeBrightness(activeThemeBrightness.value)
  setActiveThemePerformanceMode(value)
}

const setThemeAdvancedControls = (value: boolean) => {
  activeThemeAdvancedControls.value = value
  setActiveThemeAdvancedControls(value)
}

const setBusinessAllCategoryFlatMode = (value: boolean) => {
  activeBusinessAllCategoryFlatMode.value = value
  setStoredBusinessAllCategoryFlatMode(value)
}

const applyBusinessTableCardDensity = (value: BusinessTableCardDensity) => {
  const nextDensity = isBusinessTableCardDensity(value) ? value : "standard"
  activeBusinessTableCardDensity.value = nextDensity
  setStoredBusinessTableCardDensity(nextDensity)
}

const applyBusinessProductCardViewMode = (value: BusinessProductCardViewMode) => {
  const nextViewMode = isBusinessProductCardViewMode(value) ? value : "standard"
  activeBusinessProductCardViewMode.value = nextViewMode
  setStoredBusinessProductCardViewMode(nextViewMode)
}

const syncLinkedBusinessDensityPreferences = () => {
  if (activeBusinessTableCardDensityLinked.value) {
    applyBusinessTableCardDensity(getBusinessDensityFromThemeDensityScale(activeThemeDensityScale.value))
  }
  if (activeBusinessProductCardDensityLinked.value) {
    applyBusinessProductCardViewMode(getBusinessProductCardViewModeFromThemeDensityScale(activeThemeDensityScale.value))
  }
}

const setBusinessTableCardDensityLinked = (value: boolean) => {
  activeBusinessTableCardDensityLinked.value = value
  setStoredBusinessTableCardDensityLinked(value)
  if (value) {
    applyBusinessTableCardDensity(getBusinessDensityFromThemeDensityScale(activeThemeDensityScale.value))
  }
}

const setBusinessProductCardDensityLinked = (value: boolean) => {
  activeBusinessProductCardDensityLinked.value = value
  setStoredBusinessProductCardDensityLinked(value)
  if (value) {
    applyBusinessProductCardViewMode(getBusinessProductCardViewModeFromThemeDensityScale(activeThemeDensityScale.value))
  }
}

const setBusinessTableCardDensity = (value: BusinessTableCardDensity, unlinkFromThemeDensity = true) => {
  if (unlinkFromThemeDensity) {
    setBusinessTableCardDensityLinked(false)
  }
  applyBusinessTableCardDensity(value)
}

const setBusinessProductCardViewMode = (value: BusinessProductCardViewMode, unlinkFromThemeDensity = true) => {
  if (unlinkFromThemeDensity) {
    setBusinessProductCardDensityLinked(false)
  }
  applyBusinessProductCardViewMode(value)
}

const resetThemeSettings = () => {
  setThemeProfile("default")
  setThemeBrightness(DEFAULT_THEME_BRIGHTNESS)
  setThemeBrightnessEnabled(DEFAULT_THEME_BRIGHTNESS_ENABLED)
  setThemePerformanceMode(DEFAULT_THEME_PERFORMANCE_MODE)
  setThemeAdvancedControls(false)
  setThemeFont("standard")
  setThemeDensity("standard")
  setBusinessAllCategoryFlatMode(false)
  setBusinessTableCardDensityLinked(true)
  setBusinessProductCardDensityLinked(true)
}

/** 在 html 根元素上挂载 class */
const setHtmlClassName = (value: ThemeName, preference: ThemePreference) => {
  if (typeof document === "undefined") {
    return
  }
  const root = document.documentElement
  REMOVABLE_THEME_CLASS_NAMES.forEach(themeName => root.classList.remove(themeName))
  if (isDarkThemeName(value)) {
    root.classList.add("dark")
  }
  root.classList.add(value)
  root.dataset.theme = value
  root.dataset.themePreference = preference
  root.style.colorScheme = isDarkThemeName(value) ? "dark" : "light"
}

const applyThemeTypography = (fontSize: ThemeFontSize, fontWeight: ThemeFontWeight) => {
  if (typeof document === "undefined") {
    return
  }
  const root = document.documentElement
  const nextFont = getThemeFontFromControls(fontSize, fontWeight)
  const fontScale = themeFontSizeScaleMap[fontSize]
  activeThemeFont.value = nextFont
  THEME_FONT_CLASS_NAMES.forEach(className => root.classList.remove(className))
  root.classList.add(`theme-font-${nextFont}`)
  root.dataset.themeFont = nextFont
  root.dataset.themeFontSize = String(fontSize)
  root.dataset.themeFontWeight = String(fontWeight)
  root.style.setProperty("--tea-font-size-caption", fontScale.caption)
  root.style.setProperty("--tea-font-size-body-sm", fontScale.bodySm)
  root.style.setProperty("--tea-font-size-body-md", fontScale.bodyMd)
  root.style.setProperty("--tea-font-size-body-lg", fontScale.bodyLg)
  root.style.setProperty("--tea-font-size-title-lg", fontScale.titleLg)
  root.style.setProperty("--tea-font-size-headline-md", fontScale.headlineMd)
  root.style.setProperty("--tea-font-size-stat", fontScale.stat)
  root.style.setProperty("--tea-font-size-metric", fontScale.metric)
  root.style.setProperty("--tea-font-weight-regular", String(fontWeight))
  root.style.setProperty("--tea-font-weight-medium", String(Math.min(fontWeight + 100, 700)))
  root.style.setProperty("--tea-font-weight-semibold", String(Math.min(fontWeight + 200, 700)))
}

const applyThemeDensity = (value: ThemeDensityName) => {
  if (typeof document === "undefined") {
    return
  }
  activeThemeDensity.value = value
  const root = document.documentElement
  THEME_DENSITY_CLASS_NAMES.forEach(className => root.classList.remove(className))
  root.classList.add(`theme-density-${value}`)
  root.dataset.themeDensity = value
}

const applyThemeDensityScale = (value: ThemeDensityScale) => {
  applyThemeDensity(getThemeDensityNameFromScale(value))
}

const applyThemeSurfaceEffect = (value: ThemeSurfaceEffectName) => {
  if (typeof document === "undefined") {
    return
  }
  activeThemeSurfaceEffect.value = value
  const root = document.documentElement
  THEME_SURFACE_EFFECT_CLASS_NAMES.forEach(className => root.classList.remove(className))
  root.classList.add(`theme-surface-${value}`)
  root.dataset.themeSurfaceEffect = value
}

const applyThemePerformanceMode = (value: boolean) => {
  if (typeof document === "undefined") {
    return
  }
  activeThemePerformanceMode.value = value
  const root = document.documentElement
  root.classList.toggle("theme-performance", value)
  root.dataset.themePerformance = value ? "enabled" : "disabled"
}

const applyThemeBrightness = (value: number) => {
  if (typeof document === "undefined") {
    return
  }
  const nextBrightness = normalizeThemeBrightness(value)
  const root = document.documentElement
  const shouldApplyBrightness = !activeThemePerformanceMode.value
    && activeThemeBrightnessEnabled.value
    && nextBrightness !== DEFAULT_THEME_BRIGHTNESS
  root.dataset.themeBrightness = String(nextBrightness)
  root.dataset.themeBrightnessFilter = shouldApplyBrightness ? "enabled" : "disabled"
  if (shouldApplyBrightness) {
    root.style.filter = `brightness(${nextBrightness / 100})`
  } else {
    root.style.removeProperty("filter")
  }
}

const ensureSystemThemeListener = () => {
  if (systemThemeListenerRegistered || typeof window === "undefined" || !window.matchMedia) {
    return
  }
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
  const handleSystemThemeChange = () => {
    if (activeThemeName.value === SYSTEM_THEME_NAME) {
      applyTheme(SYSTEM_THEME_NAME)
    }
  }
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", handleSystemThemeChange)
  } else {
    mediaQuery.addListener(handleSystemThemeChange)
  }
  systemThemeListenerRegistered = true
}

/** 主题 hook */
export function useTheme() {
  return {
    themeList,
    themeFontList,
    themeProfileList,
    themeFontSizeList,
    themeFontWeightList,
    themeDensityList,
    themeDensityScaleList,
    themeSurfaceEffectList,
    businessTableCardDensityList,
    businessProductCardViewModeList,
    themeColorPresets,
    themeColorPresetValues,
    activeThemeName,
    resolvedThemeName,
    activeThemeProfile,
    activeThemeColor,
    activeThemeFont,
    activeThemeFontSize,
    activeThemeFontWeight,
    activeThemeDensity,
    activeThemeDensityScale,
    activeThemeSurfaceEffect,
    activeThemeBrightness,
    activeThemeBrightnessEnabled,
    activeThemePerformanceMode,
    activeThemeAdvancedControls,
    activeBusinessAllCategoryFlatMode,
    activeBusinessTableCardDensity,
    activeBusinessProductCardViewMode,
    activeBusinessTableCardDensityLinked,
    activeBusinessProductCardDensityLinked,
    initTheme,
    setThemeProfile,
    setTheme,
    setThemeColor,
    setThemeFont,
    setThemeFontSize,
    setThemeFontWeight,
    setThemeDensity,
    setThemeDensityScale,
    setThemeSurfaceEffect,
    setThemeBrightness,
    setThemeBrightnessEnabled,
    setThemePerformanceMode,
    setThemeAdvancedControls,
    setBusinessAllCategoryFlatMode,
    setBusinessTableCardDensity,
    setBusinessProductCardViewMode,
    setBusinessTableCardDensityLinked,
    setBusinessProductCardDensityLinked,
    resetThemeColor,
    resetThemeSettings,
  }
}
